import React from "react";
import Text from "@src/lib/ui/Text";
import Spinner from "@src/lib/ui/Spinner";
import { cn } from "@src/lib/utils/cn";
import { AgentActivityScope } from "./agentActivityStore";
import {
  useAgentActivity,
  useCurrentCourseAgentScope,
  useShowAgentActivity,
} from "./useAgentActivity";

type Props = {
  className?: string;
  /**
   * The content whose agent activity this indicator reflects (e.g.
   * courseAgentScope(courseId)). Defaults to the course open in the current
   * builder view. The store is partitioned per scope so sibling courses
   * generating in the same module don't light up each other's indicators.
   */
  scope?: AgentActivityScope;
  /**
   * Override the scope's activity signal. Use when the indicator reflects a
   * computed signal instead (e.g. the module header shows module-level
   * generation progress).
   */
  active?: boolean;
  /**
   * Explicit trailing detail (e.g. "Generating content 2/5"). When provided it
   * is shown instead of the scope's per-change label. Pass null to show no
   * detail.
   */
  detail?: string | null;
};

const AgentActivityIndicator: React.FC<Props> = ({
  className,
  scope,
  active,
  detail,
}) => {
  const currentCourseScope = useCurrentCourseAgentScope();
  const effectiveScope = scope ?? currentCourseScope;
  const scopeShow = useShowAgentActivity(effectiveScope);
  const show = active ?? scopeShow;
  const { isRecentChange, latest } = useAgentActivity(effectiveScope);

  if (!show) return null;

  const detailText =
    detail !== undefined
      ? detail
      : isRecentChange && latest
        ? latest.label
        : null;

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-full bg-purple-1 px-2 py-0.5 text-purple-9",
        className,
      )}
      role="status"
      aria-live="polite"
      data-testid="agent-activity-indicator"
    >
      <Spinner size={10} className="text-purple-9" />
      <Text type="P3" className="text-purple-9">
        Agent is working
      </Text>
      {detailText && (
        <Text type="P3" className="text-purple-7">
          · {detailText}
        </Text>
      )}
    </div>
  );
};

export default AgentActivityIndicator;
